/** Phase 14 Wave 3 — Module I — Feature Flags admin page. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { clearFeatureFlagCache } from "@/hooks/useFeatureFlag";

const CATEGORY_LABELS = {
  deal_type: "Deal Types",
  feature: "Features",
  integration: "Integrations",
  operations: "Operations",
};

export default function AdminFeatureFlags() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/feature-flags");
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (key, enabled) => {
    setBusyKey(key);
    try {
      await api.patch(`/admin/feature-flags/${key}`, { enabled });
      clearFeatureFlagCache();
      toast.success(`${key} → ${enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setBusyKey(null); }
  };
  const grouped = items.reduce((acc, f) => {
    const c = f.category || "feature";
    (acc[c] = acc[c] || []).push(f);
    return acc;
  }, {});
  return (
    <div className="space-y-6" data-testid="admin-feature-flags">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600">Module I</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">Feature flags</h1>
          <p className="mt-1 text-sm text-slate-500">Toggle product surfaces on/off without a deploy. All changes are audit-logged.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ff-refresh">
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {loading && items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>
      ) : (
        Object.keys(grouped).sort().map((cat) => (
          <div key={cat} className="space-y-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{CATEGORY_LABELS[cat] || cat}</h2>
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              {grouped[cat].map((f) => (
                <div key={f.key} data-testid={`ff-row-${f.key}`} className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0">
                  <div className="min-w-0">
                    <p className="font-mono text-xs font-semibold text-slate-900">{f.key}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{f.description}</p>
                  </div>
                  <Switch
                    checked={!!f.enabled}
                    disabled={busyKey === f.key}
                    onCheckedChange={(v) => toggle(f.key, v)}
                    data-testid={`ff-toggle-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
